import React, { useState, useMemo } from 'react';
import { Upload, Image as ImageIcon, Search, Trash2, Edit, Download, FolderPlus, X, Grid3x3, List } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

export const MediaLibraryPage = () => {
  const [mediaItems, setMediaItems] = useState([
    {
      id: 1,
      name: 'hero-banner.jpg',
      type: 'image',
      url: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3',
      size: '2.4 MB',
      dimensions: '1920x1080',
      altText: 'Trading dashboard hero image',
      folder: 'Banners',
      uploadedAt: '2026-01-15',
    },
    {
      id: 2,
      name: 'logo.png',
      type: 'image',
      url: 'https://via.placeholder.com/200x200',
      size: '45 KB',
      dimensions: '200x200',
      altText: 'PropFirmHouse logo',
      folder: 'Brand Assets',
      uploadedAt: '2026-01-10',
    },
    {
      id: 3,
      name: 'favicon.ico',
      type: 'image',
      url: 'https://via.placeholder.com/32x32',
      size: '4 KB',
      dimensions: '32x32',
      altText: 'Site favicon',
      folder: 'Brand Assets',
      uploadedAt: '2026-01-10',
    },
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleteMedia, setDeleteMedia] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Form state for editing
  const [editingMedia, setEditingMedia] = useState(null);
  const [editAltText, setEditAltText] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const folders = ['All', 'Banners', 'Brand Assets', 'Blog Images', 'Firm Logos', 'Icons'];

  const filteredMedia = useMemo(() => {
    return mediaItems.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.altText?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFolder = selectedFolder === 'all' || item.folder === selectedFolder;
      return matchesSearch && matchesFolder;
    });
  }, [mediaItems, searchTerm, selectedFolder]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const newMedia = {
        id: Date.now() + Math.random(),
        name: file.name,
        type: file.type.startsWith('image') ? 'image' : 'file',
        url: URL.createObjectURL(file),
        size: `${(file.size / 1024).toFixed(0)} KB`,
        dimensions: 'Processing...',
        altText: '',
        folder: selectedFolder === 'all' ? 'Uncategorized' : selectedFolder,
        uploadedAt: new Date().toISOString().split('T')[0],
      };
      setMediaItems(prev => [newMedia, ...prev]);
    });
    toast.success(`${files.length} file(s) uploaded successfully`);
    setUploadDialogOpen(false);
  };

  const handleEdit = (media) => {
    setEditingMedia(media);
    setEditAltText(media.altText || '');
    setEditTitle(media.name);
    setEditDescription(media.description || '');
  };

  const handleSaveEdit = () => {
    setMediaItems(mediaItems.map(m => 
      m.id === editingMedia.id 
        ? { ...m, altText: editAltText, name: editTitle, description: editDescription }
        : m
    ));
    toast.success('Media updated successfully');
    setEditingMedia(null);
  };

  const handleDelete = () => {
    setMediaItems(mediaItems.filter(m => m.id !== deleteMedia.id));
    toast.success('Media deleted successfully');
    setDeleteMedia(null);
    setSelectedMedia(null);
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    folders.push(newFolderName);
    toast.success(`Folder "${newFolderName}" created`);
    setNewFolderOpen(false);
    setNewFolderName('');
  };

  return (
    <div>
      <PageHeader
        title="Media Library"
        description="Manage images, logos, favicons, and other media files"
        breadcrumbs={['Admin', 'Media Library']}
      />

      {/* Toolbar */}
      <Card className="rounded-xl border-slate-200 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by filename or alt text..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Select value={selectedFolder} onValueChange={setSelectedFolder}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Folders</SelectItem>
                {folders.filter(f => f !== 'All').map(folder => (
                  <SelectItem key={folder} value={folder}>{folder}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-1 border border-slate-200 rounded-md p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-8 px-3"
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8 px-3"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Button onClick={() => setNewFolderOpen(true)} variant="outline">
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </Button>

            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Files
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Media Grid/List */}
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              {filteredMedia.length} {filteredMedia.length === 1 ? 'item' : 'items'}
            </p>
          </div>

          {filteredMedia.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">No media files found</p>
              <p className="text-sm text-slate-500">Upload files to get started</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredMedia.map((media) => (
                <div
                  key={media.id}
                  className="group relative border-2 border-slate-200 rounded-lg overflow-hidden hover:border-blue-400 transition-all cursor-pointer"
                  onClick={() => setSelectedMedia(media)}
                >
                  <div className="aspect-square bg-slate-100 flex items-center justify-center">
                    {media.type === 'image' ? (
                      <img src={media.url} alt={media.altText} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="h-12 w-12 text-slate-400" />
                    )}
                  </div>
                  <div className="p-2 bg-white">
                    <p className="text-xs font-medium text-slate-900 truncate">{media.name}</p>
                    <p className="text-xs text-slate-500">{media.size}</p>
                  </div>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="secondary"
                      className="h-7 w-7 shadow-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(media);
                      }}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMedia.map((media) => (
                <div
                  key={media.id}
                  className="flex items-center gap-4 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedMedia(media)}
                >
                  <div className="h-12 w-12 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                    {media.type === 'image' ? (
                      <img src={media.url} alt={media.altText} className="w-full h-full object-cover rounded" />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{media.name}</p>
                    <p className="text-xs text-slate-500">{media.size} • {media.dimensions}</p>
                  </div>
                  <Badge variant="secondary">{media.folder}</Badge>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(media);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Select Files</Label>
              <Input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">Supports: JPG, PNG, GIF, SVG, WebP</p>
            </div>
            <div>
              <Label>Upload to Folder</Label>
              <Select value={selectedFolder} onValueChange={setSelectedFolder}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {folders.filter(f => f !== 'All').map(folder => (
                    <SelectItem key={folder} value={folder}>{folder}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Folder Name</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g., Product Images"
                className="mt-2"
              />
            </div>
            <Button onClick={handleCreateFolder} className="w-full">
              Create Folder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Media Dialog */}
      <Dialog open={!!editingMedia} onOpenChange={() => setEditingMedia(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Media</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-4">
              <div className="w-48 h-48 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                {editingMedia?.type === 'image' ? (
                  <img src={editingMedia.url} alt="" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <ImageIcon className="h-16 w-16 text-slate-400" />
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <Label>File Name</Label>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Size: {editingMedia?.size}</p>
                  <p className="text-sm text-slate-600">Dimensions: {editingMedia?.dimensions}</p>
                  <p className="text-sm text-slate-600">Uploaded: {editingMedia?.uploadedAt}</p>
                </div>
              </div>
            </div>

            <div>
              <Label>Alt Text (for SEO)</Label>
              <Input
                value={editAltText}
                onChange={(e) => setEditAltText(e.target.value)}
                placeholder="Describe the image for screen readers and SEO"
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">Important for accessibility and search engines</p>
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Additional notes about this media file"
                rows={3}
                className="mt-2"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveEdit} className="flex-1">
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(editingMedia?.url);
                  toast.success('URL copied to clipboard');
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Copy URL
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setDeleteMedia(editingMedia);
                  setEditingMedia(null);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Details Side Panel */}
      {selectedMedia && !editingMedia && (
        <div className="fixed right-0 top-0 h-full w-96 bg-white border-l border-slate-200 shadow-2xl z-50 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Media Details</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedMedia(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                {selectedMedia.type === 'image' ? (
                  <img src={selectedMedia.url} alt={selectedMedia.altText} className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <ImageIcon className="h-24 w-24 text-slate-400" />
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-slate-900">{selectedMedia.name}</p>
                <p className="text-xs text-slate-500 mt-1">{selectedMedia.folder}</p>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Size:</span>
                  <span className="font-medium">{selectedMedia.size}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Dimensions:</span>
                  <span className="font-medium">{selectedMedia.dimensions}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Uploaded:</span>
                  <span className="font-medium">{selectedMedia.uploadedAt}</span>
                </div>
                {selectedMedia.altText && (
                  <div className="pt-2">
                    <p className="text-xs text-slate-600 mb-1">Alt Text:</p>
                    <p className="text-sm text-slate-900">{selectedMedia.altText}</p>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-4">
                <Button variant="outline" className="w-full" onClick={() => handleEdit(selectedMedia)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Details
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedMedia.url);
                    toast.success('URL copied to clipboard');
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Copy URL
                </Button>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setDeleteMedia(selectedMedia)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete File
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteMedia}
        onOpenChange={() => setDeleteMedia(null)}
        title="Delete Media File"
        description={`Are you sure you want to delete "${deleteMedia?.name}"? This action cannot be undone and may break any pages using this file.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};
